import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { format } from 'date-fns';
import { adminAPI } from '../services/api';

const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Phoenix', label: 'Arizona (Phoenix)' },
  { value: 'America/Anchorage', label: 'Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
  { value: 'America/Indiana/Indianapolis', label: 'Indiana (Indianapolis)' },
  { value: 'America/Detroit', label: 'Eastern (Detroit)' },
  { value: 'America/Boise', label: 'Mountain (Boise)' },
  { value: 'America/Puerto_Rico', label: 'Atlantic (Puerto Rico)' },
];

const FREQUENCY_OPTIONS = [
  { value: '1x', label: '1x per month' },
  { value: '2x', label: '2x per month' },
  { value: '4x', label: '4x per month' },
];

const HOUR_OPTIONS = [];
for (let h = 6; h <= 22; h++) {
  const ampm = h < 12 ? 'AM' : 'PM';
  const display = h === 12 ? 12 : h > 12 ? h - 12 : h;
  HOUR_OPTIONS.push({ value: h, label: `${display}:00 ${ampm}` });
}

const ScheduledTrainingPanel = ({ userId }) => {
  const [settings, setSettings] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [sendHistory, setSendHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [settingsRes, scheduleRes, historyRes] = await Promise.all([
        adminAPI.getScheduledTrainingSettings(userId),
        adminAPI.getScheduledTrainingSchedule(userId),
        adminAPI.getScheduledTrainingSendHistory(userId, 20),
      ]);
      setSettings(settingsRes.data);
      setSchedule(scheduleRes.data?.schedule || scheduleRes.data || []);
      setSendHistory(historyRes.data?.history || historyRes.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load scheduled training data');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchData();
    }
  }, [userId, fetchData]);

  const handleSettingChange = async (field, value) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const updated = { ...settings, [field]: value };
      await adminAPI.updateScheduledTrainingSettings(userId, { [field]: value });
      setSettings(updated);
      setSuccess('Settings updated');
      // Refresh schedule since it may have changed
      if (['training_frequency', 'training_timezone', 'training_window_start', 'training_window_end', 'training_enabled'].includes(field)) {
        const scheduleRes = await adminAPI.getScheduledTrainingSchedule(userId);
        setSchedule(scheduleRes.data?.schedule || scheduleRes.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    if (!window.confirm('Send a training message to this user now?')) return;
    try {
      setSending(true);
      setError(null);
      setSuccess(null);
      await adminAPI.sendScheduledTrainingNow(userId);
      setSuccess('Training message sent successfully');
      // Refresh history
      const historyRes = await adminAPI.getScheduledTrainingSendHistory(userId, 20);
      setSendHistory(historyRes.data?.history || historyRes.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send training message');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Scheduled Training</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {settings && (
        <>
          {/* Settings */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.training_enabled || false}
                    onChange={(e) => handleSettingChange('training_enabled', e.target.checked)}
                    disabled={saving}
                  />
                }
                label="Training Enabled"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={settings.training_frequency || '1x'}
                  label="Frequency"
                  onChange={(e) => handleSettingChange('training_frequency', e.target.value)}
                  disabled={saving}
                >
                  {FREQUENCY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={settings.training_timezone || 'America/New_York'}
                  label="Timezone"
                  onChange={(e) => handleSettingChange('training_timezone', e.target.value)}
                  disabled={saving}
                >
                  {US_TIMEZONES.map((tz) => (
                    <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Delivery Window Start</InputLabel>
                <Select
                  value={settings.training_window_start ?? 8}
                  label="Delivery Window Start"
                  onChange={(e) => handleSettingChange('training_window_start', e.target.value)}
                  disabled={saving}
                >
                  {HOUR_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Delivery Window End</InputLabel>
                <Select
                  value={settings.training_window_end ?? 20}
                  label="Delivery Window End"
                  onChange={(e) => handleSettingChange('training_window_end', e.target.value)}
                  disabled={saving}
                >
                  {HOUR_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  Consecutive Failures:
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  color={settings.training_consecutive_failures >= 1 ? 'error.main' : 'text.primary'}
                >
                  {settings.training_consecutive_failures || 0}
                </Typography>
                {settings.training_consecutive_failures >= 1 && (
                  <Chip label="Warning" color="error" size="small" sx={{ ml: 1 }} />
                )}
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mb: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSendNow}
              disabled={sending}
              startIcon={sending ? <CircularProgress size={16} /> : null}
            >
              {sending ? 'Sending...' : 'Send Now'}
            </Button>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Upcoming Schedule */}
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Upcoming Scheduled Sends
          </Typography>
          {Array.isArray(schedule) && schedule.length > 0 ? (
            <TableContainer sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Scheduled Date</TableCell>
                    <TableCell>Scenario</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schedule.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        {item.scheduled_for
                          ? format(new Date(item.scheduled_for), 'MMM d, yyyy h:mm a')
                          : '-'}
                      </TableCell>
                      <TableCell>{item.scenario_name || '-'}</TableCell>
                      <TableCell><Chip label="pending" size="small" color="info" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              No upcoming scheduled sends
            </Typography>
          )}

          <Divider sx={{ mb: 3 }} />

          {/* Send History */}
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Send History
          </Typography>
          {Array.isArray(sendHistory) && sendHistory.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Scenario</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Failure Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sendHistory.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        {item.sent_at || item.created_at
                          ? format(new Date(item.sent_at || item.created_at), 'MMM d, yyyy h:mm a')
                          : '-'}
                      </TableCell>
                      <TableCell>{item.scenario_name || item.scenario || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={item.status}
                          size="small"
                          color={item.status === 'sent' ? 'success' : item.status === 'failed' ? 'error' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{item.failure_reason || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary">No send history</Typography>
          )}
        </>
      )}
    </Paper>
  );
};

export default ScheduledTrainingPanel;
